import { cloneDeep } from 'lodash';
import { IObservable, Subject } from '~/rage/utils';
import { ExchangeItemType } from '~/shared';
import {
	ExchangeState,
	IExchangeData,
	IExchangeItem,
	IExchangeItemHouse,
	IExchangeItemMoney,
	IExchangeItemNumberPlate,
	IExchangeItemVehicle,
} from './types';

export class AbstractExchangeService {
	private ownedItemsData: IExchangeItem[] = [];
	private exchangeData: IExchangeData = {
		id: '',
		state: ExchangeState.Completed,
		isCancelled: false,
		tax: 0,
		characters: [
			{
				id: '',
				name: '',
				isConfirmedExchange: false,
				isConfirmedItems: false,
				placedItems: [],
			},
			{
				id: '',
				name: '',
				isConfirmedExchange: false,
				isConfirmedItems: false,
				placedItems: [],
			},
		],
	};

	private exchangeState = new Subject<ExchangeState>(this.exchangeData.state);
	private ownedItems = new Subject<IExchangeItem[]>([]);
	private localPlayerPlacedItems = new Subject<IExchangeItem[]>([]);
	private otherPlayerPlacedItems = new Subject<IExchangeItem[]>([]);
	private localPlayerName = new Subject<string | null>(null);
	private otherPlayerName = new Subject<string | null>(null);
	private exchangeTax = new Subject<number>(0);
	private confirmedItemsState = new Subject<boolean>(false);
	private localPlayerConfirmedItems = new Subject<boolean>(false);
	private localPlayerConfirmedExchange = new Subject<boolean>(false);
	protected exchangeDelaySec = new Subject<number>(5);

	protected setOwnedItems(items: IExchangeItem[]) {
		this.ownedItemsData = items;
		this.ownedItems.next(items);
	}

	protected setData(data: IExchangeData, localCharacterId: string) {
		this.exchangeData = data;
		this.exchangeState.next(data.state);
		this.exchangeTax.next(data.tax);

		const localCharacter = data.characters.find(
			(char) => char.id === localCharacterId,
		);
		const otherCharacter = data.characters.find(
			(char) => char.id !== localCharacterId,
		);

		this.localPlayerPlacedItems.next(localCharacter?.placedItems ?? []);
		this.otherPlayerPlacedItems.next(otherCharacter?.placedItems ?? []);
		this.confirmedItemsState.next(localCharacter?.isConfirmedItems ?? false);
		this.localPlayerConfirmedItems.next(
			localCharacter?.isConfirmedItems ?? false,
		);
		this.localPlayerConfirmedExchange.next(
			localCharacter?.isConfirmedExchange ?? false,
		);
		this.localPlayerName.next(localCharacter?.name ?? null);
		this.otherPlayerName.next(otherCharacter?.name ?? null);
	}

	protected getOwnedItemsData(): IExchangeItem[] {
		return cloneDeep(this.ownedItemsData);
	}

	protected getData(): IExchangeData {
		return cloneDeep(this.exchangeData);
	}

	getExchangeState(): IObservable<ExchangeState> {
		return this.exchangeState;
	}

	getOwnedItems(): IObservable<IExchangeItem[]> {
		return this.ownedItems;
	}

	getExchangeDelaySec(): IObservable<number> {
		return this.exchangeDelaySec;
	}

	getLocalPlayerPlacedItems(): IObservable<IExchangeItem[]> {
		return this.localPlayerPlacedItems;
	}

	getOtherPlayerPlacedItems(): IObservable<IExchangeItem[]> {
		return this.otherPlayerPlacedItems;
	}

	getLocalPlayerName(): IObservable<string | null> {
		return this.localPlayerName;
	}

	getOtherPlayerName(): IObservable<string | null> {
		return this.otherPlayerName;
	}

	getExchangeTax(): IObservable<number> {
		return this.exchangeTax;
	}

	getConfirmedItemsState(): IObservable<boolean> {
		return this.confirmedItemsState;
	}

	isItemMoney(item: IExchangeItem): item is IExchangeItemMoney {
		return item.type === ExchangeItemType.Money;
	}

	isItemHouse(item: IExchangeItem): item is IExchangeItemHouse {
		return item.type === ExchangeItemType.House;
	}

	isItemVehicle(item: IExchangeItem): item is IExchangeItemVehicle {
		return item.type === ExchangeItemType.Vehicle;
	}

	isItemNumberPlate(item: IExchangeItem): item is IExchangeItemNumberPlate {
		return item.type === ExchangeItemType.NumberPlate;
	}

	isLocalPlayerConfirmedItems(): IObservable<boolean> {
		return this.localPlayerConfirmedItems;
	}

	isLocalPlayerConfirmedExchange(): IObservable<boolean> {
		return this.localPlayerConfirmedExchange;
	}
}
